/**
 * LegalPage — shared shell for the standalone /terms and /privacy routes.
 *
 * Public (renders whether or not the visitor is signed in), so the legal
 * links on the signup page resolve instead of bouncing to the dashboard.
 * Matches the v2 glass-nav shell used by PricingPage / PublicLanding.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "@d-m-organization/hub-shared-lib/auth";
import { LogoMark } from "../../components/LogoMark.js";
import { COMPANY, type LegalSection } from "./content.js";

// ── Design tokens v2 (aligned with PricingPage / PublicLanding) ────────────
const C = {
  purple: "#351DBD",
  navy: "#261F59",
  muted: "#E3EBF6",
  bg: "#f0eef6",
  ink: "#2b2a44",
  faint: "#8a88a3",
} as const;

export function LegalPage({
  title,
  sections,
}: {
  title: string;
  sections: LegalSection[];
}) {
  const { t } = useTranslation();
  const { isAuthed } = useAuth();
  const year = new Date().getFullYear();

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      {/* ── Glass nav ────────────────────────────────────────────────── */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: `1px solid ${C.muted}`,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 32px",
            height: 72,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
          }}
        >
          <Link
            to="/"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              textDecoration: "none",
              color: C.navy,
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: "-0.02em",
            }}
          >
            <LogoMark color={C.purple} size={28} />
            <span>{t("legal.nav.brand")}</span>
          </Link>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {isAuthed ? (
              <Link to="/" className="brand-btn brand-btn-primary brand-btn-sm">
                {t("legal.nav.dashboard")}
              </Link>
            ) : (
              <>
                <Link to="/login" className="brand-btn brand-btn-ghost brand-btn-sm">
                  {t("legal.nav.signIn")}
                </Link>
                <Link to="/signup" className="brand-btn brand-btn-primary brand-btn-sm">
                  {t("legal.nav.getStarted")}
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── Document ─────────────────────────────────────────────────── */}
      <main style={{ maxWidth: 780, margin: "0 auto", padding: "56px 32px 72px" }}>
        <header style={{ marginBottom: 8 }}>
          <h1
            style={{
              fontSize: "clamp(28px, 4vw, 36px)",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: C.navy,
              margin: 0,
            }}
          >
            {title}
          </h1>
          <p style={{ fontSize: 14, color: C.faint, margin: "10px 0 0" }}>
            {t("legal.lastUpdated", { date: COMPANY.lastUpdated })}
          </p>
        </header>

        <div style={{ marginTop: 40 }}>
          {sections.map((s) => (
            <section key={s.title} style={{ marginBottom: 36 }}>
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: C.navy,
                  letterSpacing: "-0.01em",
                  margin: "0 0 12px",
                }}
              >
                {s.title}
              </h2>
              {s.body?.map((p, i) => (
                <p
                  key={i}
                  style={{
                    fontSize: 15,
                    lineHeight: 1.7,
                    color: C.ink,
                    margin: "0 0 12px",
                  }}
                >
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul
                  style={{
                    margin: "4px 0 0",
                    paddingLeft: 22,
                    color: C.ink,
                    fontSize: 15,
                    lineHeight: 1.7,
                  }}
                >
                  {s.bullets.map((b, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* Cross-link to the sibling legal page. */}
        <div
          style={{
            marginTop: 48,
            paddingTop: 24,
            borderTop: `1px solid ${C.muted}`,
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            fontSize: 14,
          }}
        >
          <Link to="/terms" style={{ color: C.purple, textDecoration: "none", fontWeight: 600 }}>
            {t("legal.links.terms")}
          </Link>
          <Link to="/privacy" style={{ color: C.purple, textDecoration: "none", fontWeight: 600 }}>
            {t("legal.links.privacy")}
          </Link>
        </div>
      </main>

      {/* ── Footer bar ───────────────────────────────────────────────── */}
      <footer style={{ borderTop: `1px solid ${C.muted}`, padding: "28px 0" }}>
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 32px",
            fontSize: 12,
            color: C.faint,
          }}
        >
          {t("legal.footer", { year, productName: COMPANY.productName, legalEntity: COMPANY.legalEntity, country: COMPANY.controllerCountry })}
        </div>
      </footer>
    </div>
  );
}
